import { collection, doc, getCountFromServer, getDoc } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  IWorkJourneyManagementAttentionListParams,
  IWorkJourneyManagementAttentionListResponse,
  IWorkJourneyManagementCalendarListParams,
  IWorkJourneyManagementCalendarListResponse,
  IWorkJourneyManagementDayReportDetailParams,
  IWorkJourneyManagementDayReportDetailResponse,
  IWorkJourneyManagementDayReportParams,
  IWorkJourneyManagementDayReportResponse,
  IWorkJourneyManagementJourneysListParams,
  IWorkJourneyManagementJourneysListResponse,
  IWorkJourneyManagementMatrixListParams,
  IWorkJourneyManagementMatrixListResponse,
  IWorkJourneyReplacementCandidatesListParams,
  IWorkJourneyReplacementCandidatesListResponseDto,
} from '@src/models/interfaces/workJourneyManagement.interface';
import type { WorkJourneyWorkShift } from '@src/packages/workJourney/types';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { buildRestUrl } from '@src/util/cloud-functions/restEndpoint';

export type IWorkJourneyConfigImpactSnapshot = {
  id: string;
  name: string;
  requiredMembers: number;
  workShift: WorkJourneyWorkShift | null;
};

export type PlanMonthlyJourneyCallableParams = {
  organizationId: string;
  workJourneyConfigId: string;
  requiredMembers: number;
  address: Record<string, unknown> | null;
  workShift: Record<string, unknown> | null;
  locationType?: string | null;
  shiftStartDate: string;
  shiftEndDate: string;
  supervisors: Array<{ id: string; user: Record<string, unknown> }>;
  members: Array<{ id: string; user: Record<string, unknown> }>;
  movedMembers?: Array<{
    userId: string;
    fromWorkJourneyConfigId: string;
    fromWorkJourneyConfigName?: string | null;
  }>;
};

const EMPTY_ATTENTION_RESPONSE: IWorkJourneyManagementAttentionListResponse = {
  items: [],
  total: 0,
};

const EMPTY_JOURNEYS_RESPONSE: IWorkJourneyManagementJourneysListResponse = {
  summary: { month: 0, year: 0, issues: [] },
  items: [],
  total: 0,
};

const EMPTY_CALENDAR_RESPONSE: IWorkJourneyManagementCalendarListResponse = {
  summary: {
    month: 0,
    year: 0,
    issues: [
      { type: 'below_minimum', count: 0 },
      { type: 'scheduled_absences', count: 0 },
      { type: 'rejections', count: 0 },
      { type: 'holiday', count: 0 },
    ],
  },
  days: [],
};

const EMPTY_REPLACEMENT_CANDIDATES_RESPONSE: IWorkJourneyReplacementCandidatesListResponseDto = {
  users: [],
  total: 0,
};

class WorkJourneyManagementRepository {
  async listAttentionItems(
    params: IWorkJourneyManagementAttentionListParams,
  ): Promise<IWorkJourneyManagementAttentionListResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementAttentionListParams,
      IWorkJourneyManagementAttentionListResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_ATTENTION);
    const response = await callableFn(params);
    return response.data ?? EMPTY_ATTENTION_RESPONSE;
  }

  async listJourneys(
    params: IWorkJourneyManagementJourneysListParams,
  ): Promise<IWorkJourneyManagementJourneysListResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementJourneysListParams,
      IWorkJourneyManagementJourneysListResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_JOURNEYS);
    const response = await callableFn(params);
    return response.data ?? EMPTY_JOURNEYS_RESPONSE;
  }

  async listCalendar(
    params: IWorkJourneyManagementCalendarListParams,
  ): Promise<IWorkJourneyManagementCalendarListResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementCalendarListParams,
      IWorkJourneyManagementCalendarListResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_CALENDAR);
    const response = await callableFn(params);
    return response.data ?? {
      ...EMPTY_CALENDAR_RESPONSE,
      summary: {
        ...EMPTY_CALENDAR_RESPONSE.summary,
        month: params.month,
        year: params.year,
      },
    };
  }

  async listDayReport(
    params: IWorkJourneyManagementDayReportParams,
  ): Promise<IWorkJourneyManagementDayReportResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementDayReportParams,
      IWorkJourneyManagementDayReportResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_DAY_REPORT);
    const response = await callableFn(params);
    return (
      response.data ?? {
        date: params.date,
        summary: {
          present: 0,
          justifiedAbsence: 0,
          unjustifiedAbsence: 0,
          journeys: 0,
          people: 0,
        },
        items: [],
        total: 0,
      }
    );
  }

  async listDayReportDetail(
    params: IWorkJourneyManagementDayReportDetailParams,
  ): Promise<IWorkJourneyManagementDayReportDetailResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementDayReportDetailParams,
      IWorkJourneyManagementDayReportDetailResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_DAY_REPORT_DETAIL);
    const response = await callableFn(params);
    return (
      response.data ?? {
        date: params.date,
        workJourneyConfigId: params.workJourneyConfigId,
        items: [],
        total: 0,
      }
    );
  }

  async listMonthMatrix(
    params: IWorkJourneyManagementMatrixListParams,
  ): Promise<IWorkJourneyManagementMatrixListResponse> {
    const callableFn = apiCallable<
      IWorkJourneyManagementMatrixListParams,
      IWorkJourneyManagementMatrixListResponse
    >(API_OPERATIONS.LIST_WORK_JOURNEY_MANAGEMENT_MONTH_MATRIX);
    const response = await callableFn(params);
    return (
      response.data ?? {
        config: {
          id: params.workJourneyConfigId,
          name: '',
          organizationId: params.organizationId,
          requiredMembers: 0,
          assignedMembers: 0,
          shiftDescription: '',
          state: 'active',
        },
        month: params.month,
        year: params.year,
        days: [],
        users: [],
        cells: [],
        coverage: [],
      }
    );
  }

  async listReplacementCandidates(
    params: IWorkJourneyReplacementCandidatesListParams,
  ): Promise<IWorkJourneyReplacementCandidatesListResponseDto> {
    const callableFn = apiCallable<
      IWorkJourneyReplacementCandidatesListParams,
      IWorkJourneyReplacementCandidatesListResponseDto
    >(API_OPERATIONS.LIST_REPLACEMENT_CANDIDATES);
    const response = await callableFn(params);
    return response.data ?? EMPTY_REPLACEMENT_CANDIDATES_RESPONSE;
  }

  async getWorkJourneyConfigSnapshot(
    organizationId: string,
    workJourneyConfigId: string,
  ): Promise<IWorkJourneyConfigImpactSnapshot | null> {
    if (!organizationId || !workJourneyConfigId) return null;

    const configRef = doc(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.WORK_JOURNEY_CONFIGS,
      workJourneyConfigId,
    );
    const snap = await getDoc(configRef);
    if (!snap.exists()) return null;

    const data = snap.data() as {
      name?: string;
      requiredMembers?: number;
      workShift?: WorkJourneyWorkShift | null;
    };

    return {
      id: snap.id,
      name: data.name ?? snap.id,
      requiredMembers: Number(data.requiredMembers) || 0,
      workShift: data.workShift ?? null,
    };
  }

  async getWorkJourneyMembersCount(
    organizationId: string,
    workJourneyConfigId: string,
  ): Promise<number> {
    if (!organizationId || !workJourneyConfigId) return 0;

    const membersRef = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.WORK_JOURNEY_CONFIGS,
      workJourneyConfigId,
      FSCollections.WORK_JOURNEY_MEMBERS,
    );
    const aggregate = await getCountFromServer(membersRef);
    return aggregate.data().count;
  }

  async ignoreRejectedShiftChange(params: {
    organizationId: string;
    eventId: string;
  }): Promise<{ success: boolean; message?: string }> {
    const callableFn = apiCallable<
      { organizationId: string; eventId: string },
      { success: boolean; message?: string }
    >(API_OPERATIONS.IGNORE_REJECTED_SHIFT_CHANGE);
    const response = await callableFn(params);
    return response.data ?? { success: false };
  }

  async resendRejectedShiftChange(params: {
    organizationId: string;
    eventId: string;
  }): Promise<{ success: boolean; message?: string }> {
    const callableFn = apiCallable<
      { organizationId: string; eventId: string },
      { success: boolean; message?: string }
    >(API_OPERATIONS.RESEND_REJECTED_SHIFT_CHANGE);
    const response = await callableFn(params);
    return response.data ?? { success: false };
  }

  async planMonthlyJourney(params: PlanMonthlyJourneyCallableParams): Promise<{
    success: boolean;
    pendingNotices: number;
    message?: string;
  }> {
    const callableFn = apiCallable<
      PlanMonthlyJourneyCallableParams,
      { success: boolean; pendingNotices: number; message?: string }
    >(API_OPERATIONS.PLAN_MONTHLY_JOURNEY);
    const response = await callableFn(params);
    return response.data ?? { success: false, pendingNotices: 0 };
  }

  async createMatrixAttendance(params: {
    organizationId: string;
    workJourneyConfigId: string;
    userId: string;
    date: string;
  }): Promise<{ attendanceRecordId: string; created: boolean }> {
    const callableFn = apiCallable<
      {
        organizationId: string;
        workJourneyConfigId: string;
        userId: string;
        date: string;
      },
      { attendanceRecordId: string; created: boolean }
    >(API_OPERATIONS.CREATE_MATRIX_ATTENDANCE);
    const response = await callableFn(params);
    if (!response.data?.attendanceRecordId) {
      throw new Error('Unable to create attendance record for matrix day');
    }
    return response.data;
  }

  async rejectMembershipChangeByToken(token: string): Promise<{
    ok: boolean;
    status: number;
    data?: { error?: string; errorCode?: string };
  }> {
    if (!token) {
      return {
        ok: false,
        status: 401,
        data: { error: 'Unauthenticated' },
      };
    }

    const endpoint = buildRestUrl('/workJourney/rejectMembershipChange');
    // Prefer Bearer so the JWT is not logged in proxy/history via `?token=`.
    // The HTTP CF also accepts query/body token for email deep-links.
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    let data: { error?: string; errorCode?: string } | undefined;
    try {
      data = (await response.json()) as { error?: string; errorCode?: string };
    } catch {
      data = undefined;
    }

    return { ok: response.ok, status: response.status, data };
  }
}

export default WorkJourneyManagementRepository;
